import * as React from "react";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MuiToolbar from "@mui/material/Toolbar";
import { tabsClasses } from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import InputBase from "@mui/material/InputBase";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";

import SideMenuMobile from "./SideMenuMobile";
import MenuButton from "./MenuButton";
import ColorModeIconDropdown from "../../shared-theme/ColorModeIconDropdown";
import StaticCurrentDate from "./CustomDatePicker";
import { removeToken } from "../../Services/LocalStorageService";
import { stopTokenWatcher } from "../../Services/tokenHelper";

const Toolbar = styled(MuiToolbar)({
  width: "100%",
  padding: "12px",
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  justifyContent: "center",
  gap: "12px",
  flexShrink: 0,
  [`& ${tabsClasses.flexContainer}`]: {
    gap: "8px",
    p: "8px",
    pb: 0,
  },
});

const SearchWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#f5f7fa",
  borderRadius: "10px",
  padding: "6px 14px",
  gap: "8px",
  width: "100%",
  border: "1px solid",
  borderColor: theme.palette.divider,
  transition: "all 0.3s ease",
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}15`,
  },
}));

export default function AppNavbar() {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const dropdownOpen = Boolean(anchorEl);

  // ✅ ADDED - Track sidebar state
  const [sidebarOpen, setSidebarOpen] = React.useState(
    localStorage.getItem("sidebarOpen") !== "false",
  );

  // ✅ ADDED - Listen for sidebar toggle event
  React.useEffect(() => {
    const handleToggle = () => {
      setSidebarOpen(localStorage.getItem("sidebarOpen") !== "false");
    };
    window.addEventListener("sidebarToggle", handleToggle);
    return () => window.removeEventListener("sidebarToggle", handleToggle);
  }, []);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const handleDropdownOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleCompanyProfile = () => {
    navigate("/dashboard/CompanyProfile");
    handleDropdownClose();
  };

  const handleLogout = () => {
    stopTokenWatcher();
    localStorage.clear();
    removeToken();
    navigate("/");
    handleDropdownClose();
  };

  const userName = localStorage.getItem("name") || "User";
  const userInitial = userName?.charAt(0)?.toUpperCase() || "U";
  const userRole = localStorage.getItem("role") || "User";

  return (
    <>
      {/* ============================================ */}
      {/* ✅ MOBILE TOP BAR */}
      {/* ============================================ */}
      <AppBar
        position="fixed"
        sx={{
          display: { xs: "flex", md: "none" },
          boxShadow: 0,
          bgcolor: "#fff",
          backgroundImage: "none",
          borderBottom: "1px solid",
          borderColor: "divider",
          top: "var(--template-frame-height, 0px)",
           zIndex: (theme) => theme.zIndex.drawer + 1, 
        }}
      >
        <Toolbar variant="regular">
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              flexGrow: 1,
              width: "100%",
              gap: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "center",
                mr: "auto",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                }}
              >
                SIDDHARTH INDUSTRIES PVT. LTD.
              </Typography>
            </Stack>

            <MenuButton aria-label="menu" onClick={toggleDrawer(true)}>
              <MenuRoundedIcon />
            </MenuButton>
            <SideMenuMobile open={open} toggleDrawer={toggleDrawer} />
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ============================================ */}
      {/* ✅ DESKTOP TOP BAR - Dynamic Width */}
      {/* ============================================ */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: "none", md: "flex" },
          bgcolor: "#fff",
          backgroundImage: "none",
          borderBottom: "1px solid",
          borderColor: "divider",
          top: "var(--template-frame-height, 0px)",
          ml: sidebarOpen ? "210px" : "64px",
          width: sidebarOpen ? "calc(100% - 210px)" : "calc(100% - 64px)",

          // ✅ ADDED - Smooth transition
          transition: (theme) =>
            theme.transitions.create(["margin", "width"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.standard,
            }),

          zIndex: (theme) => theme.zIndex.drawer + 1, 
        }}
      >
        <MuiToolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            minHeight: "64px !important",
            gap: 2,
          }}
        >
          {/* LEFT - Search Bar */}
          <Box sx={{ flex: "0 1 400px" }}>
            <SearchWrapper>
              <SearchRoundedIcon
                sx={{ color: "text.secondary", fontSize: 20 }}
              />
              <InputBase
                placeholder="Search anything..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                sx={{
                  flex: 1,
                  fontSize: "0.875rem",
                  "& input": {
                    padding: 0,
                    color: "text.primary",
                    "&::placeholder": {
                      color: "text.secondary",
                      opacity: 0.7,
                    },
                  },
                }}
              />
            </SearchWrapper>
          </Box>

          {/* RIGHT - Actions */}
          <Stack direction="row" alignItems="center" gap={1.5}>
            <StaticCurrentDate />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                p: 0.3,
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "#f5f7fa",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                },
              }}
            >
              <ColorModeIconDropdown />
            </Box>

            <Stack
              direction="row"
              alignItems="center"
              gap={1.2}
              onClick={handleDropdownOpen}
              sx={{
                cursor: "pointer",
                borderRadius: "10px",
                px: 1.2,
                py: 0.6,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: (theme) =>
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.05)"
                    : "#f5f7fa",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: "primary.main",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  border: "2px solid",
                  borderColor: "primary.light",
                }}
              >
                {userInitial}
              </Avatar>
              <Stack alignItems="flex-end">
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.2,
                    fontSize: "0.8rem",
                    letterSpacing: 0.3,
                  }}
                >
                  {userName.toUpperCase()}
                </Typography>
                <Chip
                  label={userRole}
                  size="small"
                  color="primary"
                  sx={{
                    height: "15px",
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    "& .MuiChip-label": { px: 0.8 },
                  }}
                />
              </Stack>
            </Stack>

            {/* DROPDOWN MENU */}
            <Menu
              anchorEl={anchorEl}
              open={dropdownOpen}
              onClose={handleDropdownClose}
              onClick={handleDropdownClose}
              disableScrollLock 
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1.5,
                  minWidth: 220,
                  borderRadius: "12px",
                  overflow: "visible",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
                  backgroundColor: "background.paper",
                  p: 0.5,

                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: -6,
                    right: 20,
                    width: 12,
                    height: 12,
                    bgcolor: "background.paper",
                    transform: "rotate(45deg)",
                    zIndex: 0,
                    borderTop: "1px solid",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                  },

                  "& .MuiMenuItem-root": {
                    borderRadius: "8px",
                    mx: 0.5,
                    my: 0.4,
                    px: 1.5,
                    py: 1,
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.2s ease",

                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.06)"
                          : "rgba(25, 118, 210, 0.08)",
                      borderColor: "primary.main",
                      transform: "translateX(2px)",
                    },

                    "& .MuiListItemIcon-root": {
                      minWidth: 34,
                    },
                  },
                },
              }}
            >
              <MenuItem onClick={handleCompanyProfile}>
                <ListItemIcon>
                  <BusinessRoundedIcon
                    fontSize="small"
                    sx={{ color: "primary.main" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Company Profile"
                  primaryTypographyProps={{
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    color: "text.primary",
                  }}
                />
              </MenuItem>

              <MenuItem
                onClick={handleLogout}
                sx={{
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(244, 67, 54, 0.12) !important"
                        : "rgba(244, 67, 54, 0.08) !important",
                    borderColor: "error.main !important",
                  },
                }}
              >
                <ListItemIcon>
                  <LogoutRoundedIcon
                    fontSize="small"
                    sx={{ color: "error.main" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    color: "error.main",
                  }}
                />
              </MenuItem>
            </Menu>
          </Stack>
        </MuiToolbar>
      </AppBar>
    </>
  );
}
